/**
 * Utilitaires de formatage pour les données
 */
import { CATEGORY_COLORS, DATE_FORMATS, DEFAULT_IMAGES, IMAGE_EXTENSIONS } from '../config/constants';

/**
 * Formate une date en français
 * @param {string} dateString - Date à formater
 * @param {Object} options - Options de formatage
 * @returns {string} Date formatée
 */
export const formatDate = (dateString, options = DATE_FORMATS.SHORT) => {
  return new Date(dateString).toLocaleDateString('fr-FR', options);
};

/**
 * Formate un article pour l'affichage dans les listes
 * @param {Object} article - Article à formater
 * @returns {Object} Article formaté
 */
export const formatArticleForDisplay = (article) => {
  // Vérification de base
  if (!article) {
    console.error('Article undefined dans formatArticleForDisplay');
    return null;
  }

  // Construire l'URL de l'image
  let imageUrl = DEFAULT_IMAGES.ARTICLE_COVER;
  
  if (article.cover_image) {
    imageUrl = getPocketBaseFileUrl(article, article.cover_image);
  }

  // Gestion sécurisée des catégories (unique ou multiple)
  let categoryName = "Article";
  let categoryColor = "blue";
  
  if (article.expand?.category) {
    // Si category est un tableau (catégories multiples)
    if (Array.isArray(article.expand.category)) {
      // Prendre la première catégorie pour l'affichage principal
      categoryName = article.expand.category[0]?.name || "Article";
      categoryColor = getCategoryColor(categoryName);
    } else {
      // Si category est un objet unique (ancienne structure)
      categoryName = article.expand.category.name || "Article";
      categoryColor = getCategoryColor(categoryName);
    }
  } else if (article.category_name) {
    categoryName = article.category_name;
    categoryColor = getCategoryColor(categoryName);
  }

  return {
    id: article.id,
    url: `/article/${article.slug || article.id}`, // Utiliser le slug si disponible, sinon l'ID
    title: article.title || "Article sans titre",
    avatar: DEFAULT_IMAGES.AVATAR,
    author: "Equipe - Edutio", 
    date: formatDate(article.created || new Date().toISOString()),
    image: imageUrl,
    image2x: imageUrl, 
    category: categoryColor,
    categoryContent: categoryName,
    // Ajouter toutes les catégories pour usage avancé
    allCategories: getAllCategoriesFromArticle(article),
    excerpt: article.excerpt || "",
    slug: article.slug || "",
    content: article.content || "",
    created: article.created,
    updated: article.updated
  };
};

/**
 * Extrait toutes les catégories d'un article
 * @param {Object} article - Article avec catégories
 * @returns {Array} Tableau des catégories avec nom et couleur
 */
export const getAllCategoriesFromArticle = (article) => {
  if (!article?.expand?.category) {
    return [];
  }

  // Si category est un tableau (catégories multiples)
  if (Array.isArray(article.expand.category)) {
    return article.expand.category.map(cat => ({
      id: cat.id,
      name: cat.name,
      color: getCategoryColor(cat.name)
    }));
  } else {
    // Si category est un objet unique (ancienne structure)
    return [{
      id: article.expand.category.id,
      name: article.expand.category.name,
      color: getCategoryColor(article.expand.category.name)
    }];
  }
};

/**
 * Retourne une couleur de catégorie basée sur le nom
 * @param {string} categoryName - Nom de la catégorie
 * @returns {string} Classe CSS de couleur
 */
export const getCategoryColor = (categoryName) => {
  // Gestion robuste des valeurs nulles/undefined
  if (!categoryName || typeof categoryName !== 'string') {
    return CATEGORY_COLORS.default;
  }
  
  const normalizedName = categoryName.toLowerCase().trim();
  
  // Si le nom est vide après normalisation
  if (!normalizedName) {
    return CATEGORY_COLORS.default;
  }
  
  // Si la catégorie est dans notre mapping, l'utiliser
  if (CATEGORY_COLORS[normalizedName]) {
    return CATEGORY_COLORS[normalizedName];
  }
  
  // Sinon, assigner une couleur basée sur le hash du nom pour la cohérence
  const availableColors = ['red', 'green', 'blue', 'purple', 'pink', 'orange'];
  const hash = normalizedName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const colorIndex = hash % availableColors.length;
  
  return availableColors[colorIndex];
};

/**
 * Tronque un texte à une longueur donnée
 * @param {string} text - Texte à tronquer
 * @param {number} maxLength - Longueur maximale
 * @returns {string} Texte tronqué
 */
export const truncateText = (text, maxLength = 150) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
};

/**
 * Génère un slug à partir d'un titre
 * @param {string} title - Titre à convertir
 * @returns {string} Slug généré
 */
export const generateSlug = (title) => {
  return title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Supprime les accents
    .replace(/[^a-z0-9\s-]/g, '') // Supprime les caractères spéciaux
    .replace(/\s+/g, '-') // Remplace les espaces par des tirets
    .replace(/-+/g, '-') // Supprime les tirets multiples
    .trim('-'); // Supprime les tirets en début et fin
};

/**
 * Valide une URL d'image
 * @param {string} url - URL à valider
 * @returns {boolean} True si l'URL est valide
 */
export const isValidImageUrl = (url) => {
  if (!url) return false;
  return IMAGE_EXTENSIONS.test(url) || url.startsWith('data:image/');
};

/**
 * Retourne une image par défaut si l'URL n'est pas valide
 * @param {string} imageUrl - URL de l'image
 * @param {string} defaultImage - Image par défaut
 * @returns {string} URL de l'image à utiliser
 */
export const getImageUrl = (imageUrl, defaultImage = DEFAULT_IMAGES.ARTICLE_COVER) => {
  return isValidImageUrl(imageUrl) ? imageUrl : defaultImage;
};

/**
 * Construit l'URL complète pour un fichier PocketBase
 * @param {Object} record - Enregistrement PocketBase
 * @param {string} filename - Nom du fichier
 * @returns {string} URL complète du fichier
 */
export const getPocketBaseFileUrl = (record, filename) => {
  if (!filename || !record) {
    return DEFAULT_IMAGES.ARTICLE_COVER;
  }
  
  const baseUrl = process.env.REACT_APP_POCKETBASE_URL || 'https://edutio.fr/api/';
  // Supprimer le slash final s'il existe
  const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  
  // Format correct basé sur l'exemple fourni : 
  // https://edutio.fr/api/api/files/pbc_4287850865/ndrmilytjoo7ilz/filename.png
  
  if (record.collectionId) {
    // Utiliser le collectionId (format correct)
    return `${cleanBaseUrl}/api/files/${record.collectionId}/${record.id}/${filename}`;
  } else {
    // Fallback avec collectionName
    return `${cleanBaseUrl}/api/files/${record.collectionName}/${record.id}/${filename}`;
  }
};
